"use client";

import Link from "next/link";
import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import AivelLogo from "../project_components/specific/aivel_logo";
import LoginForm from "./loginform";
import { aivelFont } from "@/styles/fonts";
import { NoiseOverlay } from "../project_components/general/wooble";

type ViewType = "login" | "reset-password" | "reset-sent";

function LoginContent() {
  const [currentView, setCurrentView] = useState<ViewType>("login");
  const searchParams = useSearchParams();

  // URL fragment와 query parameter 오류 처리
  useEffect(() => {
    // Query parameter 오류 처리
    const error = searchParams.get("error");
    const message = searchParams.get("message");

    if (error && message) {
      const decodedMessage = decodeURIComponent(message);

      if (error === "otp_expired") {
        toast.error(
          "비밀번호 재설정 링크가 만료되었습니다. 새로운 재설정 링크를 요청해주세요.",
        );
      } else if (error === "access_denied") {
        toast.error("인증이 거부되었습니다. 다시 시도해주세요.");
      } else if (error === "session_expired") {
        toast.error("인증 코드가 만료되었습니다. 다시 시도해주세요.");
      } else {
        toast.error(decodedMessage);
      }
    }

    // Fragment 오류 처리 (클라이언트에서만 가능)
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash.includes("error=")) {
        const params = new URLSearchParams(hash.substring(1));
        const fragmentError = params.get("error");
        const fragmentErrorCode = params.get("error_code");
        const fragmentErrorDescription = params.get("error_description");

        if (fragmentErrorCode === "otp_expired") {
          toast.error(
            "비밀번호 재설정 링크가 만료되었습니다. 새로운 재설정 링크를 요청해주세요.",
          );
        } else if (fragmentError === "access_denied") {
          toast.error("인증이 거부되었습니다. 다시 시도해주세요.");
        } else if (fragmentErrorDescription) {
          toast.error(
            decodeURIComponent(fragmentErrorDescription.replace(/\+/g, " ")),
          );
        }

        // Clean URL by removing the fragment
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.search,
        );
      }
    }
  }, [searchParams]);

  const getTitle = () => {
    switch (currentView) {
      case "reset-password":
        return "비밀번호 재설정";
      case "reset-sent":
        return "메일을 확인해주세요";
      default:
        return "Sign in to AIVEL";
    }
  };

  const getDescription = () => {
    switch (currentView) {
      case "reset-password":
        return "가입하신 이메일을 입력하세요";
      case "reset-sent":
        return "비밀번호 재설정 링크를 발송드렸습니다";
      default:
        return "Enter your email and password to login.";
    }
  };
  return (
    <main className="h-screen w-screen flex overflow-hidden">
      <NoiseOverlay className="relative w-1/2 h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex-col justify-center items-center px-12 hidden md:flex">
        <p
          className={`${aivelFont.className} absolute top-4 left-4 text-xl text-zinc-600 dark:text-zinc-400`}
        >
          {"{ AIVEL }"}
        </p>
        <div className="relative flex items-center justify-center flex-1">
          <div className="relative w-64 h-64">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/20 to-purple-500/20 backdrop-blur-sm"></div>
            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-300/10 to-purple-400/10"></div>
            <div className="absolute inset-8 rounded-full bg-white/5"></div>
          </div>

          <div className="absolute top-16 left-16 w-8 h-8 rounded-full bg-blue-400/30"></div>
          <div className="absolute top-32 right-20 w-4 h-4 rounded-full bg-purple-400/40"></div>
          <div className="absolute bottom-24 left-24 w-6 h-6 rounded-full bg-indigo-400/35"></div>
          <div className="absolute bottom-16 right-16 w-3 h-3 rounded-full bg-cyan-400/50"></div>

          <div className="absolute top-1/2 left-0 w-20 h-px bg-gradient-to-r from-transparent to-blue-400/30"></div>
          <div className="absolute top-1/2 right-0 w-20 h-px bg-gradient-to-l from-transparent to-purple-400/30"></div>
          <div className="absolute left-1/2 top-0 w-px h-20 bg-gradient-to-b from-transparent to-blue-400/30"></div>
          <div className="absolute left-1/2 bottom-0 w-px h-20 bg-gradient-to-t from-transparent to-purple-400/30"></div>
        </div>
        <div className="text-center space-y-2 pb-12">
          <div className="text-white/80 text-sm font-light tracking-wider">
            AIVEL
          </div>
          <div className="text-white/40 text-xs">AI Co-writer</div>
        </div>
      </NoiseOverlay>

      {/* 오른쪽: 로그인 섹션 - 정확히 50% */}
      <div className="w-full md:w-1/2 h-full flex flex-col justify-center items-center p-8 bg-background overflow-hidden">
        <div className="max-w-md w-full space-y-6">
          <div className="flex items-start">
            <AivelLogo
              svgClass="h-8 w-8"
              rectFillClass="fill-blue-50"
              strokeThin="stroke-blue-800 stroke-[24px]"
              strokeThick="stroke-gray-800 stroke-[48px]"
            />
          </div>

          <div className="flex flex-col">
            <h1 className="text-2xl font-bold">{getTitle()}</h1>
            <p className="text-sm text-gray-400 mt-1">{getDescription()}</p>
          </div>

          <LoginForm onViewChange={setCurrentView} />

          {currentView === "login" && (
            <>
              <p className="text-xs text-gray-500 text-center mt-6">
                로그인으로 다음 사항에 동의합니다.
                <Link href="/legal/tou" className="underline">
                  이용약관
                </Link>{" "}
                /{" "}
                <Link href="/legal/pp" className="underline">
                  개인정보처리방침
                </Link>
              </p>
            </>
          )}

          <p className="text-xs text-gray-500 text-center">
            Need help?{" "}
            <a
              href="mailto:ceo@latticeai.dev"
              className="underline text-blue-600"
            >
              AIVEL 지원팀
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginContent />
    </Suspense>
  );
}
